import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { OneToOneStatus } from '../../../model/communication/Communication';
import { IListItem } from '../../../model/list/ListItem';
import { IExpertForm } from '../../../components/user-profile/expertRegistrationComponent';
import { IGroupSelect } from '../../../model/profile/groupselect';
import { IComplaintContact } from '../../../model/complaint';

export interface IRelatedItem {
  _id: string;
  _score: number;
  categories: Array<{ categoryId: string }>;
  createdAt: string;
  modifiedAt: string;
  publishState: string;
  type: string;
  content: any;
}

export interface IUserActivities {
  created: Array<IRelatedItem>;
  member: Array<IRelatedItem>;
}

export interface IDeactivatedCommunity {
  id: string;
  type: string;
  alias?: string;
  title?: string;
  activityDate?: number;
  public?: boolean;
  listed?: boolean;
  activityWarningSent?: boolean;
  deactivationDeadline?: number;
}

export type CommunityAdminStatus = 'deactivated' | 'grace';

export interface ISurveyAnswerOption {
  answerId: string;
  answerText: string;
}

export interface ISurveyAdminItem {
  id: string;
  title?: string;
  question?: string;
  /** Publish state of the survey thing. */
  published: boolean;
  /** Closed in the backoffice: no longer offered, results kept. */
  closed: boolean;
  /** The one survey currently offered after login. */
  active: boolean;
  createdAt?: string;
  answerCount: number;
  declinedCount: number;
  answers?: Array<ISurveyAnswerOption>;
}

export interface IProfileRelatedItems {
  communication?: OneToOneStatus | null;
  autoSuggest?: any;
  relatedItems?: Array<any> | any;
  pendingTokens?: Array<any>;
  pendingTokensWorking?: boolean;
  expertRegisteredMessage?: string;
  groupList?: Array<IGroupSelect>;
  deactivatedCommunities?: Array<IDeactivatedCommunity>;
  deactivatedCommunitiesWorking?: boolean;
  deactivatedCommunitiesQuery?: { q?: string; sort?: 'date' | 'alpha'; skip?: number; take?: number; status?: CommunityAdminStatus };
  recheckInactiveCommunitiesWorking?: boolean;
  repairMgmtParticipationsWorking?: boolean;
  repairMgmtParticipationsResult?: { scanned?: number; fixedParticipants?: number; fixedParticipations?: number };
  resendWarningWorkingIds?: Array<string>;
  resendWarningResult?: { id: string; recipients: number };
  surveys?: Array<ISurveyAdminItem>;
  surveysWorking?: boolean;
  /** Admin area: user that bot-answer complaints are routed to. */
  complaintContact?: IComplaintContact | null;
  complaintContactWorking?: boolean;
  complaintContactCandidates?: Array<IComplaintContact>;
  complaintContactSearching?: boolean;
}

const userProfile = createSlice({
  name: 'userProfile',
  initialState: {} as IProfileRelatedItems,
  reducers: {
    fetchPlayerAutoSuggest: (state, action: PayloadAction<string>) => {
      if (!!state.autoSuggest && state.autoSuggest.length === 0) {
        state.autoSuggest = null;
      }

      return state;
    },
    fetchPlayerAutoSuggestSuccess: (state, action: PayloadAction<any>) => {
      return { ...state, autoSuggest: action.payload };
    },
    fetchPlayerAutoSuggestFailed: state => {
      return { ...state, autoSuggest: undefined };
    },
    fetchRelatedItems: (state, action: PayloadAction<{ userId: string }>) => {
      return { ...state, relatedItems: undefined };
    },
    fetchRelatedItemsSuccess: (state, action: PayloadAction<{ created: Array<IListItem>; member: Array<IListItem> }>) => {
      return { ...state, relatedItems: action.payload };
    },
    fetchRelatedItemsFailed: state => {
      return { ...state, relatedItems: [] };
    },
    fetchActorProfile: (state, action: PayloadAction<{ alias: string }>) => {
      return { ...state, relatedItems: undefined };
    },
    fetchActorProfileSuccess: (state, action: PayloadAction<any>) => {
      return { ...state, relatedItems: action.payload };
    },
    fetchActorProfileFailed: state => {
      return { ...state, relatedItems: undefined };
    },
    userprofileCommunicationRequest: (state, action: PayloadAction<{ id: string }>) => {
      state.communication = undefined;
    },
    userprofileCommunicationSuccess: (state, action: PayloadAction<{ data: OneToOneStatus }>) => {
      state.communication = action.payload.data;
    },
    userprofileCommunicationFailed: (state, action: PayloadAction<{ message?: string }>) => {
      state.communication = null;
    },
    userprofileCommunicationCreateRequest: (state, action: PayloadAction<{ userId: string; message: string; detail?: string }>) => {},
    userprofileCommunicationCreateSuccess: (state, action: PayloadAction<{}>) => {},
    userprofileCommunicationCreateFailed: (state, action: PayloadAction<{ message?: string }>) => {},

    fetchUserprofileAdminPendingTokens: (state, action: PayloadAction<{}>) => {
      return { ...state, pendingTokensWorking: true };
    },
    fetchUserprofileAdminPendingTokensSuccess: (state, action: PayloadAction<{ pendingTokens: Array<IListItem> }>) => {
      return { ...state, pendingTokensWorking: false, pendingTokens: action.payload.pendingTokens };
    },
    fetchUserprofileAdminPendingTokensFailed: (state, action: PayloadAction<{}>) => {
      return { ...state, pendingTokensWorking: false, pendingTokens: [] };
    },

    registerAdmin: (state, action: PayloadAction<{ email: string }>) => {},
    registerExpertAdmin: (state, action: PayloadAction<{ request: IExpertForm }>) => {},
    registerExpertAdminResponse: (state, action: PayloadAction<{ response: string }>) => {
      return { ...state, expertRegisteredMessage: action.payload.response };
    },

    getAllGroupsRequest: (state, action: PayloadAction<{}>) => {
      return { ...state, groupList: [] };
    },
    getAllGroupsSuccess: (state, action: PayloadAction<{ groupList: Array<IGroupSelect> }>) => {
      return { ...state, groupList: action.payload.groupList };
    },
    getAllGroupsFailed: (state, action: PayloadAction<{ message?: string }>) => {
      return { ...state, groupList: [] };
    },

    clearRequestToken: (state, action: PayloadAction<{ tokenId: string }>) => {},
    resetAndResendNotifications: (state, action: PayloadAction<{ email: string }>) => {},
    resendNotifications: (state, action: PayloadAction<{ contentId: string, contentType: string, eventType: string }>) => {},
    alertMessage: (state, action: PayloadAction<{ userId: string, accusedId: string, alertType: string, alertMessage: string, messageId: string, messageLink: string }>) => {},

    fetchDeactivatedCommunities: (state, action: PayloadAction<{ q?: string; sort?: 'date' | 'alpha'; skip?: number; take?: number; status?: CommunityAdminStatus }>) => {
      return { ...state, deactivatedCommunitiesWorking: true, deactivatedCommunitiesQuery: action.payload };
    },
    fetchDeactivatedCommunitiesSuccess: (state, action: PayloadAction<{ items: Array<IDeactivatedCommunity>; skip: number; take: number }>) => {
      return { ...state, deactivatedCommunitiesWorking: false, deactivatedCommunities: action.payload.items };
    },
    fetchDeactivatedCommunitiesFailed: (state, action: PayloadAction<{}>) => {
      return { ...state, deactivatedCommunitiesWorking: false, deactivatedCommunities: [] };
    },
    reactivateCommunity: (state, action: PayloadAction<{ id: string }>) => {},
    recheckInactiveCommunities: (state, action: PayloadAction<{}>) => {
      return { ...state, recheckInactiveCommunitiesWorking: true };
    },
    recheckInactiveCommunitiesDone: (state, action: PayloadAction<{}>) => {
      return { ...state, recheckInactiveCommunitiesWorking: false };
    },
    repairMgmtParticipations: (state, action: PayloadAction<{}>) => {
      return { ...state, repairMgmtParticipationsWorking: true, repairMgmtParticipationsResult: undefined };
    },
    repairMgmtParticipationsDone: (state, action: PayloadAction<{ scanned?: number; fixedParticipants?: number; fixedParticipations?: number }>) => {
      return { ...state, repairMgmtParticipationsWorking: false, repairMgmtParticipationsResult: action.payload };
    },
    resendWarningEmail: (state, action: PayloadAction<{ id: string }>) => {
      const ids = (state.resendWarningWorkingIds || []).filter(x => x !== action.payload.id);
      return { ...state, resendWarningWorkingIds: [...ids, action.payload.id], resendWarningResult: undefined };
    },
    fetchComplaintContact: (state, action: PayloadAction<{}>) => {
      return { ...state, complaintContactWorking: true };
    },
    fetchComplaintContactSuccess: (state, action: PayloadAction<{ contact: IComplaintContact | null }>) => {
      return { ...state, complaintContactWorking: false, complaintContact: action.payload.contact };
    },
    fetchComplaintContactFailed: (state, action: PayloadAction<{}>) => {
      return { ...state, complaintContactWorking: false };
    },
    searchComplaintContacts: (state, action: PayloadAction<{ q: string }>) => {
      return { ...state, complaintContactSearching: true };
    },
    searchComplaintContactsSuccess: (state, action: PayloadAction<{ items: Array<IComplaintContact> }>) => {
      return { ...state, complaintContactSearching: false, complaintContactCandidates: action.payload.items };
    },
    searchComplaintContactsFailed: (state, action: PayloadAction<{}>) => {
      return { ...state, complaintContactSearching: false, complaintContactCandidates: [] };
    },
    setComplaintContact: (state, action: PayloadAction<{ userId?: string }>) => {
      return { ...state, complaintContactWorking: true };
    },
    fetchSurveys: (state, action: PayloadAction<{}>) => {
      return { ...state, surveysWorking: true };
    },
    fetchSurveysSuccess: (state, action: PayloadAction<{ items: Array<ISurveyAdminItem> }>) => {
      return { ...state, surveysWorking: false, surveys: action.payload.items };
    },
    fetchSurveysFailed: (state, action: PayloadAction<{}>) => {
      return { ...state, surveysWorking: false, surveys: [] };
    },
    resendWarningEmailDone: (state, action: PayloadAction<{ id: string; recipients?: number }>) => {
      const ids = (state.resendWarningWorkingIds || []).filter(x => x !== action.payload.id);
      return {
        ...state,
        resendWarningWorkingIds: ids,
        resendWarningResult: typeof action.payload.recipients === 'number'
          ? { id: action.payload.id, recipients: action.payload.recipients }
          : state.resendWarningResult,
      };
    },
  },
});

export const {
  fetchPlayerAutoSuggest,
  fetchPlayerAutoSuggestSuccess,
  fetchPlayerAutoSuggestFailed,
  fetchRelatedItems,
  fetchRelatedItemsSuccess,
  fetchRelatedItemsFailed,
  fetchActorProfile,
  fetchActorProfileSuccess,
  fetchActorProfileFailed,
  userprofileCommunicationRequest,
  userprofileCommunicationSuccess,
  userprofileCommunicationFailed,
  userprofileCommunicationCreateRequest,
  userprofileCommunicationCreateSuccess,
  userprofileCommunicationCreateFailed,
  fetchUserprofileAdminPendingTokens,
  fetchUserprofileAdminPendingTokensSuccess,
  fetchUserprofileAdminPendingTokensFailed,
  registerAdmin,
  registerExpertAdmin,
  registerExpertAdminResponse,
  clearRequestToken,
  getAllGroupsRequest,
  getAllGroupsSuccess,
  getAllGroupsFailed,
  resetAndResendNotifications,
  resendNotifications,
  alertMessage,
  fetchDeactivatedCommunities,
  fetchDeactivatedCommunitiesSuccess,
  fetchDeactivatedCommunitiesFailed,
  reactivateCommunity,
  recheckInactiveCommunities,
  recheckInactiveCommunitiesDone,
  repairMgmtParticipations,
  repairMgmtParticipationsDone,
  resendWarningEmail,
  resendWarningEmailDone,
  fetchSurveys,
  fetchSurveysSuccess,
  fetchSurveysFailed,
  fetchComplaintContact,
  fetchComplaintContactSuccess,
  fetchComplaintContactFailed,
  searchComplaintContacts,
  searchComplaintContactsSuccess,
  searchComplaintContactsFailed,
  setComplaintContact,
} = userProfile.actions;
export default userProfile.reducer;

